import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyHeader from '@/components/company/CompanyHeader';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import useSopState, { ISop } from '@/states/sop.state';
import useTenantState from '@/states/tenant.state';
import SearchBar from '@/components/company/filters/SearchBar';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

export default function SOP() {
  const navigate = useNavigate();
  const { currentTenant } = useTenantState();
  const { sops, getSops, bulkPublishSop, isLoading } = useSopState();

  const [searchTerm, setSearchTerm] = useState('');
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  const [sortedSops, setSortedSops] = useState<ISop[]>([]);
  const [allPublished, setAllPublished] = useState(false);

  useEffect(() => {
    if (currentTenant?.id) {
      getSops(currentTenant.id);
    }
  }, [currentTenant?.id]);

  useEffect(() => {
    if (sops.length > 0) {
      setAllPublished(sops.every(item => item.is_published === true));
    } else {
      setAllPublished(false);
    }
  }, [sops]);

  useEffect(() => {
    if (!sops.length) return;

    const sorted = [...sops].sort((a, b) => {
      const dir = pageConfig.order === 'asc' ? 1 : -1;
      const aVal = (a.name ?? '').toLowerCase();
      const bVal = (b.name ?? '').toLowerCase();
      return aVal.localeCompare(bVal) * dir;
    });

    setSortedSops(sorted);
  }, [pageConfig.orderBy, pageConfig.order, sops]);

  const filteredSops = sortedSops.filter(sop => (sop.name || '').toLowerCase().includes(searchTerm.toLowerCase()));
  const columns: MainTableColumn[] = [
    {
      name: 'name',
      label: 'SOP Name',
      render: (val: string) => <span className="font-medium text-gray-800">{val || '-'}</span>,
    },
  ];

  const handleBulkPublish = async (value: boolean) => {
    if (!currentTenant?.id) return;
    await bulkPublishSop(currentTenant.id, value);
    await getSops(currentTenant.id);
  };

  const handleViewDetail = (row: ISop) => {
    if (!currentTenant?.slug) return;
    navigate(`/${currentTenant.slug}/sop/${row.slug}`);
  };

  const handleExternalLink = (row: ISop) => {
    const slug = currentTenant?.slug ?? 'default';
    return `https://studio-onprem.merapi.alurkerja.com/${slug}/drive/modeler/bpmn/${row.slug}`;
  };

  return (
    <section className="w-full">
      <CompanyHeader
        title="Standard Operating Procedures (SOP)"
        description="View and manage your company's standard operating procedures."
        onPublish={async value => await handleBulkPublish(value)}
        isPublished={allPublished}
      />

      <div className="mx-5 border-b border-gray-200" />

      <div className="flex justify-start mt-5 px-5 gap-2">
        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search SOP by name..." className="w-72" />
      </div>

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: filteredSops as unknown as { [key: string]: unknown }[],
          totalElements: filteredSops.length,
          totalPages: 1,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        showActions
        onView={handleViewDetail}
        showExternalLink
        externalLinkUrl={handleExternalLink}
      />
    </section>
  );
}
